import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ApiGenerator {
  generator: string;
  vc: number;
  available: number;
}

interface ApiClassRow {
  time_slot__slot: number;
  time_string: string;
  generator_class: number;
  generators: ApiGenerator[];
  generator_count: number;
  quantum: number;
  total_capacity: number;
  must_run: number;
  must_run_mw: number;
  vc: number;
}

interface GeneratorListModalProps {
  isOpen: boolean;
  onClose: () => void;
  classNumber: number;
  apiData: ApiClassRow[];
}

const ModalWrapper: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isMounted ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMounted]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 999999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="fixed inset-0 bg-black transition-opacity duration-200"
        style={{ opacity: isAnimating ? 0.45 : 0 }}
        onClick={onClose}
      />
      <div
        className="relative bg-white flex flex-col overflow-hidden transition-all duration-200"
        style={{
          width: "min(960px, 95vw)",
          maxHeight: "88vh",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          zIndex: 10001,
          opacity: isAnimating ? 1 : 0,
          transform: isAnimating
            ? "translateY(0) scale(1)"
            : "translateY(8px) scale(0.97)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

const GeneratorListModal: React.FC<GeneratorListModalProps> = ({
  isOpen,
  onClose,
  classNumber,
  apiData,
}) => {
  const classRows = useMemo(
    () =>
      (apiData ?? [])
        .filter((r) => r.generator_class === classNumber)
        .sort((a, b) => a.time_slot__slot - b.time_slot__slot),
    [apiData, classNumber],
  );

  // Collect unique generator names across all slots, sorted by VC
  const generators = useMemo(() => {
    const genMap = new Map<string, number>(); // name → min vc
    for (const row of classRows) {
      for (const g of row.generators ?? []) {
        const existing = genMap.get(g.generator);
        if (existing === undefined || g.vc < existing)
          genMap.set(g.generator, g.vc);
      }
    }
    return [...genMap.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([name, vc]) => ({ name, vc }));
  }, [classRows]);

  const generatorNames = useMemo(
    () => generators.map((g) => g.name),
    [generators],
  );

  // Build lookup: slot → generatorName → available
  const slotGenMap = useMemo(() => {
    const map = new Map<number, Map<string, number>>();
    for (const row of classRows) {
      const genLookup = new Map<string, number>();
      for (const g of row.generators ?? []) {
        genLookup.set(g.generator, g.available);
      }
      map.set(row.time_slot__slot, genLookup);
    }
    return map;
  }, [classRows]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      {/* Title bar */}
      <div
        className="relative flex items-center justify-center px-5 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid #e2e8f0" }}
      >
        <h3 style={{ fontSize: "15px", fontWeight: 500, color: "#7B788C" }}>
          Generator List
        </h3>
        <button
          onClick={onClose}
          className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full transition-colors hover:bg-gray-100"
          style={{ color: "#6F6D7C" }}
          title="Close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Table area */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden p-4">
        {classRows.length === 0 || generatorNames.length === 0 ? (
          <div
            className="flex items-center justify-center flex-1 text-sm py-16"
            style={{ color: "#9B98AF" }}
          >
            No generator data available for Class {classNumber}
          </div>
        ) : (
          <div
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
            style={{ border: "1px solid #d0dde0", borderRadius: "12px" }}
          >
            {/* Group header */}
            <div
              style={{
                background: "#eaf3f5",
                color: "#34656d",
                fontWeight: 600,
                fontSize: "13px",
                textAlign: "center",
                padding: "10px 12px",
                whiteSpace: "nowrap",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                borderTop: "1px solid #34656d",
                borderLeft: "1px solid #34656d",
                borderRight: "1px solid #34656d",
                borderBottom: "1px solid #b8d4d8",
              }}
            >
              Generators
            </div>

            <div className="overflow-auto flex-1 min-h-0">
              <table
                id="bidGeneratorListTable"
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  width: "max-content",
                  minWidth: "100%",
                  fontSize: "13px",
                }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 50,
                    background: "#ffffff !important",
                  }}
                >
                  <tr>
                    {generators.map(({ name, vc }, idx) => {
                      const isLast = idx === generators.length - 1;
                      return (
                        <th
                          key={name}
                          style={{
                            background: "#ffffff",
                            color: "#34656d",
                            fontWeight: 600,
                            fontSize: "13px",
                            textAlign: "center",
                            padding: "8px 14px",
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #b8d4d8",
                            borderRight: isLast ? "none" : "1px solid #e8f0f2",
                            minWidth: "72px",
                          }}
                        >
                          {name}
                          <span
                            style={{
                              fontWeight: 400,
                              color: "#9B98AF",
                              fontSize: "11px",
                              marginLeft: "4px",
                            }}
                          >
                            (vc: {vc.toFixed(2)})
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {classRows.map((row, rowIdx) => {
                    const genLookup = slotGenMap.get(row.time_slot__slot);
                    const isLastRow = rowIdx === classRows.length - 1;
                    return (
                      <tr
                        key={row.time_slot__slot}
                        style={{ background: "#ffffff" }}
                      >
                        {generatorNames.map((name, idx) => {
                          const val = genLookup?.get(name);
                          const isLast = idx === generatorNames.length - 1;
                          return (
                            <td
                              key={name}
                              style={{
                                color:
                                  val !== undefined ? "#525062" : "#C4C2CE",
                                fontWeight: 400,
                                fontSize: "13px",
                                textAlign: "center",
                                padding: "10px 14px",
                                whiteSpace: "nowrap",
                                borderBottom: isLastRow
                                  ? "none"
                                  : "1px solid #f0f4f5",
                                borderRight: isLast
                                  ? "none"
                                  : "1px solid #e8f0f2",
                              }}
                            >
                              {val !== undefined ? val.toFixed(2) : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default GeneratorListModal;
